import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Grid,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  styled,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TableSortLabel,
  Button,
} from '@mui/material';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { visuallyHidden } from '@mui/utils';
import useCompanySettings from '../hooks/useCompanySettings';
import { useTheme } from '@mui/material/styles';
import { fetchOfficialHolidays, isHoliday } from '../utils/holidays';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';

function Attendance() {
  const theme = useTheme();
  const [entries, setEntries] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [visitorPage, setVisitorPage] = useState(0);
  const [visitorRowsPerPage, setVisitorRowsPerPage] = useState(5);
  const [filter, setFilter] = useState('daily');
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('EmployeeID');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [employeeList, setEmployeeList] = useState([]);
  const [officialHolidays, setOfficialHolidays] = useState([]);
  const [visitorEntries, setVisitorEntries] = useState([]);
  const [visitorList, setVisitorList] = useState([]);

  const settings = useCompanySettings();

  useEffect(() => {
    // Filtre değiştiğinde veya ay/yıl güncellendiğinde verileri yeniden yükle
    fetchAttendance();
    fetchAllOfficialHolidays();
    fetchVisitors();
  }, [filter, selectedMonth, selectedYear, employeeList]);

  useEffect(() => {
    // Çalışanları normalize ederek yükle (FirstName/first_name, LastName/last_name)
    // EmployeeId aslında RFID kart ID'sini içeriyor
    axios.get('https://pdks.bytebridge.com.tr/api/employees').then(res => {
      const employeesData = res.data.employees || res.data || [];
      const normalized = employeesData.map(emp => {
        // Çalışan ID'sini attendance ID'sine çevir (38 -> EMP001, 39 -> EMP002, etc.)
        const attendanceNumber = emp.ID - 37; // 38-37=1, 39-37=2, etc.
        const attendanceID = `EMP${attendanceNumber.toString().padStart(3, '0')}`;
        
        return {
          ...emp,
          FirstName: emp.FirstName || emp.first_name || emp.firstname || '',
          LastName: emp.LastName || emp.last_name || emp.lastname || '',
          EmployeeID: emp.ID || emp.id || '', // Gerçek çalışan ID'si
          RfidCardID: emp.EmployeeId || emp.employee_id || emp.EmployeeID || emp.employee_id || '', // EmployeeId aslında RFID ID'si
          AttendanceID: attendanceID // Attendance ile eşleşme için ID (38 -> EMP001)
        };
      });
      setEmployeeList(normalized);
    }).catch(err => {
      console.error('Error fetching employees:', err);
      setEmployeeList([]);
    });
  }, []);

  useEffect(() => {
    // Filtre veya ay/yıl değiştiğinde güncel zamanı al
    const today = new Date();
    if (filter === 'monthly') {
      // Aylık filtrede varsayılan olarak güncel ayı kullan
      setSelectedMonth(today.getMonth());
      setSelectedYear(today.getFullYear());
    } else if (filter === 'yearly') {
      setSelectedYear(today.getFullYear());
    }
  }, [filter]);

  const fetchAttendance = async () => {
    // Eski veriyi hemen temizle (geçişlerde kalan verileri kaldırmak için)
    setEntries([]);
    try {
      const response = await axios.get('https://pdks.bytebridge.com.tr/api/attendance');
      let attendanceData = response.data.attendance || [];

      if (filter === 'daily') {
        attendanceData = attendanceData.filter((entry) => isToday(new Date(entry.EntryTime)));
      } else if (filter === 'weekly') {
        attendanceData = attendanceData.filter((entry) => isThisWeek(new Date(entry.EntryTime)));
      } else if (filter === 'monthly') {
        // Aylık filtrede seçilen ay ve yıla göre filtrele
        attendanceData = attendanceData.filter((entry) => {
          const entryDate = new Date(entry.EntryTime);
          // Ay ve yıl kontrolü
          return entryDate.getMonth() === selectedMonth && entryDate.getFullYear() === selectedYear;
        });
        console.log(`Filtering for month: ${selectedMonth}, year: ${selectedYear}`);
        console.log(`Filtered data count: ${attendanceData.length}`);
      } else if (filter === 'yearly') {
        attendanceData = attendanceData.filter((entry) => isSpecificYear(new Date(entry.EntryTime), selectedYear));
        if (selectedMonth !== null) {
          attendanceData = attendanceData.filter((entry) =>
            isSpecificMonth(new Date(entry.EntryTime), selectedMonth, selectedYear)
          );
        }
      }

      let filteredEntries = attendanceData;

      // Tüm çalışanları göster ve onların katılım verilerini birleştir
      let allEmployeesWithAttendance = employeeList.map(employee => {
        // Çalışanın katılım kaydını bul (AttendanceID ile eşleştir)
        const attendanceRecord = filteredEntries.find(entry => 
          String(entry.EmployeeID) === String(employee.AttendanceID)
        );
        
        // Debug için eşleşme kontrolü
        if (attendanceRecord) {
          console.log(`✓ Eşleşme bulundu: ${employee.FullName} - AttendanceID: ${employee.AttendanceID} - RFID: ${employee.RfidCardID}`);
        } else {
          console.log(`✗ Eşleşme bulunamadı: ${employee.FullName} - AttendanceID: ${employee.AttendanceID} - RFID: ${employee.RfidCardID}`);
        }
        
        if (attendanceRecord) {
          // Katılım kaydı varsa, onu kullan
          return {
            ...attendanceRecord,
            FullName: `${employee.FirstName || ''} ${employee.LastName || ''}`.trim(),
            Department: employee.Department || '',
            BaseSalary: employee.BaseSalary || '',
            Position: employee.Position || '',
            Status: employee.Status || '',
            Email: employee.Email || '',
            Phone: employee.Phone || '',
            RfidCardID: employee.RfidCardID || '',
            EmployeeID: employee.EmployeeID, // Gerçek çalışan ID'sini kullan
            EntryTime: formatDate(attendanceRecord.EntryTime),
            ExitTime: formatDate(attendanceRecord.ExitTime),
            TotalHours: calculateTotalHours(attendanceRecord.EntryTime, attendanceRecord.ExitTime),
          };
        }
      }).filter(employee => employee !== undefined); // Sadece katılım kaydı olan çalışanları filtrele

      // Arama terimine göre filtrele (tüm alanlarda ara)
      if (searchTerm) {
        allEmployeesWithAttendance = allEmployeesWithAttendance.filter((entry) => 
          String(entry.EmployeeID).includes(searchTerm) ||
          (entry.RfidCardID && String(entry.RfidCardID).includes(searchTerm)) ||
          entry.FullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (entry.Department && entry.Department.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (entry.Position && entry.Position.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (entry.Status && entry.Status.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (entry.Email && entry.Email.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (entry.Phone && entry.Phone.includes(searchTerm))
        );
      }

      const sortedEntries = sortArray(allEmployeesWithAttendance, orderBy, order);
      setEntries(sortedEntries);
    } catch (error) {
      console.error('Error fetching attendance:', error);
      // Hata durumunda yine de çalışanları göster
      const fallbackEntries = employeeList.map(employee => ({
        EmployeeID: employee.EmployeeID,
        FullName: `${employee.FirstName || ''} ${employee.LastName || ''}`.trim(),
        Department: employee.Department || '',
        BaseSalary: employee.BaseSalary || '',
        Position: employee.Position || '',
        Status: employee.Status || '',
        Email: employee.Email || '',
        Phone: employee.Phone || '',
        EntryTime: '-',
        ExitTime: '-',
        TotalHours: '-',
        ID: employee.ID
      }));
      setEntries(fallbackEntries);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('tr-TR', {
      timeZone: 'UTC',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const formatDateTR = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleString('tr-TR', {
      timeZone: 'UTC',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const calculateTotalHours = (entryTime, exitTime) => {
    // Eğer giriş veya çıkış zamanı yoksa boş göster
    if (!entryTime || !exitTime) return '';
    const entry = new Date(entryTime);
    const exit = new Date(exitTime);
    // Eğer resmi tatil ise çalışma saati 0 göster
    if (isHoliday(entry, officialHolidays)) {
      return '0.00';
    }
    const totalMilliseconds = exit - entry;
    const totalHours = totalMilliseconds / (1000 * 60 * 60);
    // varsayılan saatleri settings yüklü olmasa da kullan
    const workStartStr = settings?.workStart || '09:00';
    const workEndStr = settings?.workEnd || '18:00';
    const [startHour, startMinute] = workStartStr.split(':').map(Number);
    const [endHour, endMinute] = workEndStr.split(':').map(Number);
    const workStart = new Date(entry.getFullYear(), entry.getMonth(), entry.getDate(), startHour, startMinute);
    const workEnd = new Date(exit.getFullYear(), exit.getMonth(), exit.getDate(), endHour, endMinute);
    const workHours = (workEnd - workStart) / (1000 * 60 * 60);
    // Fazla mesai göstermeden sadece toplam saati döndür
    return totalHours.toFixed(2);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (event) => {
    const newFilter = event.target.value;
    setFilter(newFilter);
    setPage(0);
    
    // Filtre değiştiğinde güncel ay ve yılı ayarla
    const today = new Date();
    if (newFilter === 'monthly') {
      setSelectedMonth(today.getMonth());
      setSelectedYear(today.getFullYear());
    } else if (newFilter === 'yearly') {
      setSelectedYear(today.getFullYear());
    }
    
    // Filtre değişikliğinden sonra verileri yeniden yükle
    setTimeout(() => {
      fetchAttendance();
    }, 100);
  };

  const handleSort = (property) => (event) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortArray = (array, property, order) => {
    return array.slice().sort((a, b) => {
      const isAsc = order === 'asc';
      if (property === 'TotalHours') {
        return isAsc ? parseFloat(a[property]) - parseFloat(b[property]) : parseFloat(b[property]) - parseFloat(a[property]);
      } else {
        return isAsc ? String(a[property]).localeCompare(String(b[property])) : String(b[property]).localeCompare(String(a[property]));
      }
    });
  };

  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
  };

  const isThisWeek = (date) => {
    const today = new Date();
    const currentDay = today.getDay(); // 0 = Pazar, 1 = Pazartesi, ..., 6 = Cumartesi
    const diff = currentDay === 0 ? 6 : currentDay - 1; // Pazartesi'yi 0 yap
    const monday = new Date(today.setDate(today.getDate() - diff));
    const sunday = new Date(today.setDate(today.getDate() - diff + 6));
    
    // Günün başlangıcını ve sonunu ayarla
    const startOfWeek = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate());
    const endOfWeek = new Date(sunday.getFullYear(), sunday.getMonth(), sunday.getDate(), 23, 59, 59);
    
    return date >= startOfWeek && date <= endOfWeek;
  };

  const isSpecificMonth = (date, month, year) => {
    // Ay kontrolü için getMonth() 0-11 arasında değer döndürür
    return date.getMonth() === month && date.getFullYear() === year;
  };

  const isSpecificYear = (date, year) => {
    return date.getFullYear() === year;
  };

  const handleExportExcel = () => {
    if (!entries || entries.length === 0) {
      alert('Aktarılacak kayıt bulunamadı!');
      return;
    }
    const excelRows = [
      ['ID', 'Personel ID', 'RFID Kart ID', 'Ad Soyad', 'Departman', 'Pozisyon', 'Durum', 'Email', 'Telefon', 'Maaş', 'Tarih ve Giriş Saati', 'Tarih ve Çıkış Saati', 'Toplam Saat']
    ];
    entries.forEach(row => {
      excelRows.push([
        row.ID || row.id || '',
        row.EmployeeID,
        row.RfidCardID || '',
        row.FullName,
        row.Department,
        row.Position,
        row.Status,
        row.Email,
        row.Phone,
        row.BaseSalary || '',
        formatDateTR(row.EntryTime),
        formatDateTR(row.ExitTime),
        row.TotalHours
      ]);
    });
    const ws = XLSX.utils.aoa_to_sheet(excelRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Giriş-Çıkışlar');
    XLSX.writeFile(wb, `giris_cikislar_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  const fetchAllOfficialHolidays = async () => {
    const holidays = await fetchOfficialHolidays();
    setOfficialHolidays(holidays);
  };

  const fetchVisitors = async () => {
    try {
      const res = await axios.get('https://pdks.bytebridge.com.tr/api/visitors');
      setVisitorList(res.data);
      fetchVisitorAttendance(res.data);
    } catch (err) {
      // Hata yönetimi
    }
  };

  const fetchVisitorAttendance = async (visitors) => {
    try {
      const attendanceRes = await axios.get('https://pdks.bytebridge.com.tr/api/attendance');
      const attendanceData = attendanceRes.data.attendance;
      // Her ziyaretçi için ilk giriş ve son çıkış zamanını bul
      const visitorAttendance = visitors.map(visitor => {
        if (!visitor.RfidCardID) return { ...visitor, FirstEntry: null, LastExit: null };
        const records = attendanceData.filter(a => String(a.EmployeeID) === String(visitor.RfidCardID));
        if (records.length === 0) return { ...visitor, FirstEntry: null, LastExit: null };
        // İlk giriş ve son çıkış bul
        const sortedByEntry = [...records].sort((a, b) => new Date(a.EntryTime) - new Date(b.EntryTime));
        const sortedByExit = [...records].sort((a, b) => new Date(b.ExitTime) - new Date(a.ExitTime));
        return {
          ...visitor,
          FirstEntry: sortedByEntry[0]?.EntryTime || null,
          LastExit: sortedByExit[0]?.ExitTime || null
        };
      });
      setVisitorEntries(visitorAttendance);
    } catch (err) {
      // Hata yönetimi
    }
  };

  // StyledTableRow ve StyledTableCell tanımları (kaldırılmıştı, geri ekleniyor)
  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
    '&:last-child td, &:last-child th': {
      border: 0,
    },
    '&:hover': {
      backgroundColor: '#f0f0f0',
    },
  }));

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    fontWeight: 'bold',
    color: '#333',
  }));

  const handleVisitorChangePage = (event, newPage) => {
    setVisitorPage(newPage);
  };

  const handleVisitorChangeRowsPerPage = (event) => {
    setVisitorRowsPerPage(parseInt(event.target.value, 10));
    setVisitorPage(0);
  };

  // Ay değişikliği için ayrı bir handler
  const handleMonthChange = (event) => {
    const newMonth = event.target.value;
    setSelectedMonth(newMonth);
    console.log(`Month changed to: ${newMonth}`);
    // Ay değişikliğinden sonra verileri yeniden yükle
    setTimeout(() => {
      fetchAttendance();
    }, 100);
  };

  // Yıl değişikliği için ayrı bir handler
  const handleYearChange = (event) => {
    const newYear = event.target.value;
    setSelectedYear(newYear);
    console.log(`Year changed to: ${newYear}`);
    // Yıl değişikliğinden sonra verileri yeniden yükle
    setTimeout(() => {
      fetchAttendance();
    }, 100);
  };

  return (
    <Box sx={{ flexGrow: 1, padding: 3 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
        <Tooltip title="Bu sayfada devam durumunuzu görebilirsiniz.">
          <InfoOutlinedIcon color="primary" style={{ marginRight: 8, cursor: 'pointer' }} />
        </Tooltip>
        <Typography variant="h4" gutterBottom>
          GİRİŞ ÇIKIŞ TAKİBİ
        </Typography>
      </div>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
        <Button variant="outlined" color="success" onClick={handleExportExcel}>
          Giriş-Çıkışları Excel'e Aktar
        </Button>
      </Box>
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
          <TextField label="Çalışan ID Ara" variant="outlined" value={searchTerm} onChange={handleSearchChange} sx={{ flex: 1, marginRight: 2 }} />
          <FormControl variant="outlined" sx={{ minWidth: 120, marginRight: 2 }}>
            <InputLabel id="filter-label">Filtre</InputLabel>
            <Select labelId="filter-label" id="filter" value={filter} onChange={handleFilterChange} label="Filtre">
              <MenuItem value="daily">Günlük</MenuItem>
              <MenuItem value="weekly">Haftalık</MenuItem>
              <MenuItem value="monthly">Aylık</MenuItem>
              <MenuItem value="yearly">Yıllık</MenuItem>
            </Select>
          </FormControl>
          {(filter === 'monthly' || filter === 'yearly') && (
            <FormControl variant="outlined" sx={{ minWidth: 120 }}>
              <InputLabel id="month-label">Ay</InputLabel>
              <Select 
                labelId="month-label" 
                id="month" 
                value={selectedMonth} 
                onChange={handleMonthChange} 
                label="Ay"
              >
                {Array.from({ length: 12 }, (_, i) => i).map((month) => (
                  <MenuItem key={month} value={month}>
                    {new Date(0, month).toLocaleString('tr-TR', { month: 'long', timeZone: 'UTC' })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {filter === 'yearly' && (
            <FormControl variant="outlined" sx={{ minWidth: 120 }}>
              <InputLabel id="year-label">Yıl</InputLabel>
              <Select 
                labelId="year-label" 
                id="year" 
                value={selectedYear} 
                onChange={handleYearChange} 
                label="Yıl"
              >
                {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
        <TableContainer 
          component={Paper} 
          elevation={3} 
          key={`employee-table-${page}`}
          sx={{ backgroundColor: theme.palette.mode === 'dark' ? '#18191a' : '#fff', color: theme.palette.mode === 'dark' ? '#fff' : '#18191a' }}>
          <Table sx={{
            minWidth: 650,
            '& tbody tr:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? '#23272f' : '#f0f0f0',
              transition: 'background-color 0.2s'
            },
            '& td, & th': {
              color: theme.palette.mode === 'dark' ? '#fff' : '#18191a',
            },
            '& th': {
              backgroundColor: theme.palette.mode === 'dark' ? '#23272f' : '#f5f5f5',
            },
          }} aria-label="simple table">
            <TableHead>
              <TableRow>
                <StyledTableCell>Sıra</StyledTableCell>
                <StyledTableCell align="right">
                  <TableSortLabel active={orderBy === 'EmployeeID'} direction={orderBy === 'EmployeeID' ? order : 'asc'} onClick={handleSort('EmployeeID')}>
                    Çalışan ID
                    {orderBy === 'EmployeeID' ? <Box component="span" sx={visuallyHidden}>{order === 'desc' ? 'sorted descending' : 'sorted ascending'}</Box> : null}
                  </TableSortLabel>
                </StyledTableCell>
                <StyledTableCell align="right">RFID Kart ID</StyledTableCell>
                <StyledTableCell align="right">Ad Soyad</StyledTableCell>
                <StyledTableCell align="right">Departman</StyledTableCell>
                <StyledTableCell align="right">Pozisyon</StyledTableCell>
                <StyledTableCell align="right">Durum</StyledTableCell>
                <StyledTableCell align="right">Email</StyledTableCell>
                <StyledTableCell align="right">Telefon</StyledTableCell>
                <StyledTableCell align="right">Maaş</StyledTableCell>
                <StyledTableCell align="right">
                  <TableSortLabel active={orderBy === 'EntryTime'} direction={orderBy === 'EntryTime' ? order : 'asc'} onClick={handleSort('EntryTime')}>
                    Tarih ve Giriş Saati
                    {orderBy === 'EntryTime' ? <Box component="span" sx={visuallyHidden}>{order === 'desc' ? 'sorted descending' : 'sorted ascending'}</Box> : null}
                  </TableSortLabel>
                </StyledTableCell>
                <StyledTableCell align="right">
                  <TableSortLabel active={orderBy === 'ExitTime'} direction={orderBy === 'ExitTime' ? order : 'asc'} onClick={handleSort('ExitTime')}>
                    Tarih ve Çıkış Saati
                    {orderBy === 'ExitTime' ? <Box component="span" sx={visuallyHidden}>{order === 'desc' ? 'sorted descending' : 'sorted ascending'}</Box> : null}
                  </TableSortLabel>
                </StyledTableCell>
                <StyledTableCell align="right">
                  <TableSortLabel active={orderBy === 'TotalHours'} direction={orderBy === 'TotalHours' ? order : 'asc'} onClick={handleSort('TotalHours')}>
                    Toplam Saat
                    {orderBy === 'TotalHours' ? <Box component="span" sx={visuallyHidden}>{order === 'desc' ? 'sorted descending' : 'sorted ascending'}</Box> : null}
                  </TableSortLabel>
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, idx) => (
                <StyledTableRow key={`${row.EmployeeID}-${row.EntryTime || row.Date || idx}`}>
                  <TableCell component="th" scope="row">
                    {page * rowsPerPage + idx + 1}
                  </TableCell>
                  <TableCell align="right">{row.EmployeeID}</TableCell>
                  <TableCell align="right">{row.RfidCardID}</TableCell>
                  <TableCell align="right">{row.FullName}</TableCell>
                  <TableCell align="right">{row.Department}</TableCell>
                  <TableCell align="right">{row.Position}</TableCell>
                  <TableCell align="right">{row.Status}</TableCell>
                  <TableCell align="right">{row.Email}</TableCell>
                  <TableCell align="right">{row.Phone}</TableCell>
                  <TableCell align="right">{row.BaseSalary ? `${row.BaseSalary.toLocaleString('tr-TR')} TL` : '-'}</TableCell>
                  <TableCell align="right">{row.EntryTime}</TableCell>
                  <TableCell align="right">{row.ExitTime}</TableCell>
                  <TableCell align="right">{row.TotalHours}</TableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination rowsPerPageOptions={[5, 10, 25]} component="div" count={entries.length} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} sx={{ marginTop: 2 }} />
      </Grid>
      <Grid item xs={12} sx={{ marginTop: 4 }}>
        <Typography variant="h4" gutterBottom>
          ZİYARETÇİ GİRİŞ ÇIKIŞ TAKİBİ
        </Typography>
        <TableContainer 
          component={Paper} 
          elevation={3} 
          key={`visitor-table-${visitorPage}`}
          sx={{ backgroundColor: theme.palette.mode === 'dark' ? '#18191a' : '#fff', color: theme.palette.mode === 'dark' ? '#fff' : '#18191a' }}>
          <Table sx={{
            minWidth: 650,
            '& tbody tr:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? '#23272f' : '#f0f0f0',
              transition: 'background-color 0.2s'
            },
            '& td, & th': {
              color: theme.palette.mode === 'dark' ? '#fff' : '#18191a',
            },
            '& th': {
              backgroundColor: theme.palette.mode === 'dark' ? '#23272f' : '#f5f5f5',
            },
          }} aria-label="simple table">
            <TableHead>
              <TableRow>
                <StyledTableCell>Sıra</StyledTableCell>
                <StyledTableCell align="right">Ziyaretçi Adı</StyledTableCell>
                <StyledTableCell align="right">Şirket</StyledTableCell>
                <StyledTableCell align="right">Ziyaret Nedeni</StyledTableCell>
                <StyledTableCell align="right">RFID Kart ID</StyledTableCell>
                <StyledTableCell align="right">İlk Giriş</StyledTableCell>
                <StyledTableCell align="right">Son Çıkış</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visitorEntries.slice(visitorPage * visitorRowsPerPage, visitorPage * visitorRowsPerPage + visitorRowsPerPage).map((row, idx) => (
                <StyledTableRow key={`${row.ID || row.RfidCardID}-${idx}`}>
                  <TableCell component="th" scope="row">{visitorPage * visitorRowsPerPage + idx + 1}</TableCell>
                  <TableCell align="right">{row.VisitorName}</TableCell>
                  <TableCell align="right">{row.Company}</TableCell>
                  <TableCell align="right">{row.VisitReason}</TableCell>
                  <TableCell align="right">{row.RfidCardID}</TableCell>
                  <TableCell align="right">{row.FirstEntry ? formatDate(row.FirstEntry) : '-'}</TableCell>
                  <TableCell align="right">{row.LastExit ? formatDate(row.LastExit) : '-'}</TableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination rowsPerPageOptions={[5, 10, 25]} component="div" count={visitorEntries.length} rowsPerPage={visitorRowsPerPage} page={visitorPage} onPageChange={handleVisitorChangePage} onRowsPerPageChange={handleVisitorChangeRowsPerPage} sx={{ marginTop: 2 }} />
      </Grid>
    </Box>
  );
}

export default Attendance;